import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link } from 'react-router-dom';
import { useLazyGetPaginatedShipmentsQuery } from '../../../redux/commonRTK';
import { parseDateAndTime } from '../../../helpers/UtilityHelper';

const CLONE_SUFFIXES = ['C1', 'C2', 'C3', 'C4'];

const excludes =
  'returnInfo,itemList.hsn,deliveryTime,itemList.itemUrl,itemList.itemDescription,actualDeliveryTat,proshipDeliveryTat,courierDeliveryTat,lastStatusUpdateTime,epd,lifeCycleComplete,courier,merchantDetail,courierId,version,order_history,orderManifest,invoiceUrl,courierPricingRTO,courierPricing,overriddenCourierAccountCode,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,createdByUser,skipRule,modifiedByUser,rvp_reason,channel_name,customer_detail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchant,merchantPricing.merchantRateCard,merchantPricing.pricingObj,courierPricingRTO,modifiedByUser,lastModifiedDate,merchantPricingRTO.merchantRateCard,merchantPricingRTO.pricingObj,customerDetail';

const ClonedOrdersModal = ({ open, onClose, reference, defaultFilters = [] }) => {
  const [getPaginatedShipments] = useLazyGetPaginatedShipmentsQuery();
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!open || !reference) return;

    setIsLoading(true);
    setResults([]);

    const calls = CLONE_SUFFIXES.map((suffix) => {
      const clonedRef = `${reference}-${suffix}`;
      return getPaginatedShipments({
        offset: 0,
        limit: 10,
        andfilter: [
          ...defaultFilters,
          { field: 'reference', operator: 'eq', value: clonedRef },
        ],
        excludes,
        skipCount: true,
        sort: { field: 'awbRegisteredDate', direction: 'DESC' },
      })
        .unwrap()
        .then((res) => ({ suffix, clonedRef, order: res?.data?.[0] || null }))
        .catch(() => ({ suffix, clonedRef, order: null }));
    });

    Promise.all(calls)
      .then((arr) => setResults(arr.filter((r) => r.order)))
      .finally(() => setIsLoading(false));
  }, [open, reference, getPaginatedShipments]);

  const handleClose = () => {
    setResults([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Cloned Orders</Typography>
          {reference && (
            <Typography variant="caption" color="text.secondary">
              Source reference: {reference}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Looking up cloned orders…
            </Typography>
          </Box>
        ) : results.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No cloned orders found for this reference.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Clone</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>AWB</TableCell>
                  <TableCell>Courier</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map(({ suffix, clonedRef, order }) => (
                  <TableRow key={suffix} hover>
                    <TableCell>
                      <Chip size="small" label={suffix.toUpperCase()} color="warning" />
                    </TableCell>
                    <TableCell>{order.reference || clonedRef}</TableCell>
                    <TableCell>{order.awb_number || '-'}</TableCell>
                    <TableCell>{order?.courierDetail?.parent || '-'}</TableCell>
                    <TableCell>{order.createdDate ? parseDateAndTime(order.createdDate) : '-'}</TableCell>
                    <TableCell>
                      {order.orderStatus ? <Chip size="small" label={order.orderStatus} /> : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        size="small"
                        color="warning"
                        component={Link}
                        to={`/order-detail?waybill=${order.awb_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        disabled={!order.awb_number}
                      >
                        Details
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClonedOrdersModal;
